
import { useEffect, useRef, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { IsEmpty, IsNotEmpty } from "../Helpers";
import { useErrorDisplay } from '../components/ErrorDisplay';
import BusySpinner from "../components/BusySpinner";
import OnlyForRole from '../components/OnlyForRole';
import PositionAutocompleteControl from '../components/PositionAutocompleteControl';
import { Talent } from '../models/dtos';
import TalentClient, { TalentErrors } from '../apiClients/TalentClient';
import SystemUserAutocompleteControl from '../components/SystemUserAutocompleteControl';
import { Securables } from '../Enums';

export interface DashboardTalentCardProps {
    talentId: string;
    onClose?: () => Promise<void> | void;
}

const DashboardTalentCard = ({ talentId, onClose = async () => { } }: DashboardTalentCardProps) => {

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const initialized = useRef<boolean>();
    const [talent, setTalent] = useState<Talent>();
    const [talentHasChanged, setTalentHasChanged] = useState<boolean>(false);
    const [pendingStatus, setPendingStatus] = useState<boolean>();

    const loadData = async () => {
        try {
            setBusy(true);

            let t = await new TalentClient().GetSingle(talentId);
            setTalent(t);

        } catch (error) {
            setError(error, TalentErrors.GetSingle);
        }
        finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (initialized.current)
            return;

        initialized.current = true;
        loadData();
    });

    const handleSubmit = async () => {
        try {
            setBusy(true);

            await new TalentClient().Update(talent);
            setTalentHasChanged(false);
            await onClose();

        } catch (error) {
            setError(error, TalentErrors.Update);
        }
        finally {
            setBusy(false);
        }
    };

    const handleInputChange = e => {
        const { name, value } = e.target
        setTalent({ ...talent, [name]: value })
        setTalentHasChanged(true);
    };

    const handleUserSelect = (positionId) => {
        setTalent({ ...talent, PositionId: positionId })
        setTalentHasChanged(true);
    }

    const handleAssessorSelect = (positionId) => {
        setTalent({ ...talent, AssessorId: positionId })
        setTalentHasChanged(true);
    }

    const handleStatusConfirm = async () => {
        if (pendingStatus === undefined)
            return;

        try {
            setBusy(true);
            const client = new TalentClient();
            if (pendingStatus)
                await client.Reactivate(talentId);
            else
                await client.Deactivate(talentId);

            setPendingStatus(undefined);
            await loadData();
            await onClose();
        } catch (error) {
            setError(error, pendingStatus ? TalentErrors.Reactivate : TalentErrors.Deactivate);
        } finally {
            setBusy(false);
        }
    };

    const isActive = talent?.IsActive !== false;
    const activating = pendingStatus === true;

    return (
        <Box
            data-testid="talent-editor-form"
            sx={{
                width: '100%',
                maxWidth: 760,
                mx: 'auto',
                px: { xs: 2, sm: 3 },
                py: { xs: 2, sm: 2.5 },
            }}
        >
            <ErrorDisplay />
            <BusySpinner busy={busy}>
                <Stack spacing={2.25}>
                    <OnlyForRole role={Securables.talent_delete}>
                        <Box sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 2,
                        }}>
                            <FormControlLabel
                                control={<Switch
                                    checked={Boolean(talent && isActive)}
                                    disabled={!talent}
                                    inputProps={{ 'aria-label': 'Active teammate' }}
                                    onChange={(_, checked) => setPendingStatus(checked)}
                                />}
                                label={<Typography fontWeight={600}>Active teammate</Typography>}
                            />
                            <Typography color="text.secondary" variant="body2">
                                Active teammates are included in subscription billing. Deactivation preserves all teammate history and prior activation periods.
                            </Typography>
                        </Box>
                    </OnlyForRole>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                                <TextField label="First Name" fullWidth
                                    name="FirstName"
                                    error={IsEmpty(talent?.FirstName)}
                                    size='small'
                                    variant="outlined"
                                    value={talent?.FirstName ?? ''}
                                    onChange={handleInputChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                                <TextField label="Last Name" fullWidth
                                    name="LastName"
                                    error={IsEmpty(talent?.LastName)}
                                    size='small'
                                    variant="outlined"
                                    value={talent?.LastName ?? ''}
                                    onChange={handleInputChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                                <TextField label="Email" fullWidth
                                    name="Email"
                                    error={IsEmpty(talent?.Email)}
                                    size='small'
                                    variant="outlined"
                                    value={talent?.Email ?? ''}
                                    onChange={handleInputChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                                <TextField label="Org Identifier" fullWidth
                                    name="OrganizationIdentifier"
                                    size='small'
                                    variant="outlined"
                                    value={(IsNotEmpty(talent?.OrganizationIdentifier)) ? talent.OrganizationIdentifier : 'Not Assigned'}
                                    onChange={handleInputChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box>
                                <PositionAutocompleteControl positionId={talent?.PositionId} onUserSelect={handleUserSelect} />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SystemUserAutocompleteControl systemUserId={talent?.AssessorId} placeholderText="Assign Assessor" onUserSelect={handleAssessorSelect} />
                        </Grid>
                    </Grid>
                        <Box
                            data-testid="talent-editor-actions"
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                borderTop: 1,
                                borderColor: 'divider',
                                pt: 2,
                            }}
                        >
                            <Button color="primary" variant='contained' disabled={!talentHasChanged} onClick={handleSubmit}>Save changes</Button>
                        </Box>
                </Stack>
            </BusySpinner>
            <Dialog open={pendingStatus !== undefined} onClose={() => setPendingStatus(undefined)} fullWidth maxWidth="sm">
                    <DialogTitle>{activating ? 'Reactivate teammate?' : 'Deactivate teammate?'}</DialogTitle>
                    <DialogContent dividers>
                        <Alert severity={activating ? 'info' : 'warning'} sx={{ mb: 2 }}>
                            {activating
                                ? 'This teammate will immediately return to the active, billable teammate count.'
                                : 'Billing continues through the current paid period. This teammate will be excluded from future billing periods while inactive.'}
                        </Alert>
                        <Typography>
                            {activating
                                ? 'All previous activation periods and historical records will remain available.'
                                : 'The teammate will be removed from the active list, but all historical data and activation periods will be retained.'}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setPendingStatus(undefined)} color="inherit">Cancel</Button>
                        <Button
                            onClick={handleStatusConfirm}
                            color={activating ? 'primary' : 'warning'}
                            variant="contained">
                            {activating ? 'Reactivate' : 'Deactivate'}
                        </Button>
                    </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DashboardTalentCard;
